import { useState } from "react";
import { testDataSourceFetch } from "./leafHelpers";
import { getListKeysFromArray } from "./DynamicValueResolver";
import { Field, SH, Toggle } from "./UIHelper";
import { T } from "./exports";
import { ParamRowsEditor } from "./ParamRowUI";
import { LiveUrlPreview } from "./LiveUrlPreview";
import { mkDataSource } from "./Makers";

export function DataSourcesPanel({ dataSources = [], onChange, sourcePreviewData = {}, setSourcePreviewData, allInputVars = [] }) {
  const [testingById, setTestingById] = useState({});
  const [testResultById, setTestResultById] = useState({});
  const set = (next) => onChange(next);
  const add = (type = "api") => set([...(dataSources || []), mkDataSource(type)]);
  const del = (id) => set((dataSources || []).filter((s) => s.id !== id));
  const upd = (id, key, value) => set((dataSources || []).map((s) => (s.id === id ? { ...s, [key]: value } : s)));

  const runTest = async (source) => {
    if (!source) return;
    setTestingById((p) => ({ ...p, [source.id]: true }));
    setTestResultById((p) => ({ ...p, [source.id]: null }));
    try {
      const data = await testDataSourceFetch(source);
      const keys = Array.isArray(data)
        ? getListKeysFromArray(data)
        : data && typeof data === "object"
          ? Object.keys(data)
          : [];
      setTestResultById((p) => ({ ...p, [source.id]: { ok: true, count: Array.isArray(data) ? data.length : (data == null ? 0 : 1), keys, sample: data } }));
      if (setSourcePreviewData) {
        setSourcePreviewData((prev) => ({ ...prev, [source.id]: data }));
      }
    } catch (e) {
      setTestResultById((p) => ({ ...p, [source.id]: { ok: false, error: String(e?.message || e) } }));
    }
    setTestingById((p) => ({ ...p, [source.id]: false }));
  };

  const clearTest = (sourceId) => {
    setTestResultById((p) => {
      const n = { ...p };
      delete n[sourceId];
      return n;
    });
    if (setSourcePreviewData) {
      setSourcePreviewData((prev) => {
        const n = { ...prev };
        delete n[sourceId];
        return n;
      });
    }
  };

  return (
    <div style={{ padding: "0 8px 8px", overflowY: "auto", flex: 1 }}>
      <SH ch="Global Data Sources" color={T.blue} />
      <div style={{ fontSize: 8, color: T.textMute, marginBottom: 8, lineHeight: 1.55 }}>
        These sources are kiosk-wide. Data is fetched once and can be reused by any screen/group.
      </div>

      {(dataSources || []).map((src, i) => (
        <div key={src.id} style={{ background: T.bgCard, border: `1px solid ${T.blue}33`, borderRadius: 5, padding: 8, marginBottom: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <span style={{ fontSize: 9, fontWeight: 700, color: T.blue }}>📦 Source #{i + 1}</span>
            <button onClick={() => del(src.id)} style={{ background: "none", border: "none", color: T.red, cursor: "pointer", fontSize: 11, padding: 0 }}>✕</button>
          </div>

          <Field label="Name" value={src.name || ""} onChange={(v) => upd(src.id, "name", v)} placeholder="e.g. flightsFeed" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3 }}>
            <Field label="Type" value={src.type || "api"} onChange={(v) => upd(src.id, "type", v)} opts={["api", "rss", "googleSheets", "custom"]} />
            <Field label="Store As" value={src.storeAs || ""} onChange={(v) => upd(src.id, "storeAs", v)} placeholder="optional var name" />
          </div>

          {(src.type === "api" || src.type === "custom") && <>
            <Field label="URL" value={src.url || ""} onChange={(v) => upd(src.id, "url", v)} placeholder="https://api.example.com/data" />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3 }}>
              <Field label="Method" value={src.method || "GET"} onChange={(v) => upd(src.id, "method", v)} opts={["GET", "POST", "PUT", "PATCH", "DELETE"]} />
              <Field label="Response Key" value={src.responseKey || ""} onChange={(v) => upd(src.id, "responseKey", v)} placeholder="data.items" />
            </div>

            <div style={{ fontSize: 8, color: T.textSub, marginBottom: 3, marginTop: 4 }}>Params &amp; Body Fields</div>
            <ParamRowsEditor
              params={src.params || []}
              onChange={v => upd(src.id, "params", v)}
              allInputVars={allInputVars}
              method={src.method || "GET"}
              showTypeToggle={true}
            />
            <LiveUrlPreview baseUrl={src.url || ""} params={src.params || []} allInputVars={allInputVars} />

            <div style={{ fontSize: 8, color: T.textSub, marginBottom: 3, marginTop: 6 }}>Headers</div>
            <ParamRowsEditor
              params={src.headers || []}
              onChange={v => upd(src.id, "headers", v)}
              allInputVars={allInputVars}
              showTypeToggle={false}
              addLabel="+ Add Header"
              presetButtons={[{ label: "+ Add Authorization", row: { key: "Authorization", source: "literal", literalValue: "Bearer " } }]}
            />
          </>}

          {src.type === "rss" && <>
            <Field label="RSS URL" value={src.rssFeedUrl || ""} onChange={(v) => upd(src.id, "rssFeedUrl", v)} placeholder="https://example.com/feed.xml" />
            <Field label="Max Items" type="number" min={1} max={500} value={src.maxItems || 20} onChange={(v) => upd(src.id, "maxItems", v)} />
          </>}

          {src.type === "googleSheets" && <>
            <Field label="Google Sheet URL" value={src.sheetUrl || ""} onChange={(v) => upd(src.id, "sheetUrl", v)} placeholder="https://docs.google.com/spreadsheets/d/..." />
            <Field label="Sheet GID" value={src.sheetGid || "0"} onChange={(v) => upd(src.id, "sheetGid", v)} placeholder="0" />
          </>}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3 }}>
            <Toggle label="Enabled" value={src.enabled !== false} onChange={(v) => upd(src.id, "enabled", v)} />
            <Toggle label="Polling" value={src.pollEnabled || false} onChange={(v) => upd(src.id, "pollEnabled", v)} />
          </div>
          <Toggle label="Fetch on screen load" value={src.fetchOnScreenLoad !== false} onChange={(v) => upd(src.id, "fetchOnScreenLoad", v)} />
          {src.pollEnabled && <Field label="Poll Interval (sec)" type="number" min={1} value={src.pollIntervalSec ?? 60} onChange={(v) => upd(src.id, "pollIntervalSec", Number.isFinite(Number(v)) && Number(v) > 0 ? Number(v) : 60)} />}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, marginTop: 4 }}>
            <button onClick={() => runTest(src)} disabled={!!testingById[src.id]} style={{ width: "100%", padding: 5, background: "#0d2420", border: `1px solid ${T.green}44`, borderRadius: 4, color: T.green, cursor: "pointer", fontSize: 9 }}>
              {testingById[src.id] ? "⟳ Testing..." : "▶ Test Source"}
            </button>
            <button onClick={() => clearTest(src.id)} style={{ width: "100%", padding: 5, background: "#1c1008", border: `1px solid ${T.yellow}44`, borderRadius: 4, color: T.yellow, cursor: "pointer", fontSize: 9 }}>
              ✕ Clear Test
            </button>
          </div>

          {(testResultById[src.id] || sourcePreviewData[src.id] !== undefined) && (() => {
            const testResult = testResultById[src.id];
            const raw = sourcePreviewData[src.id];
            const keys = testResult?.keys || (Array.isArray(raw) ? getListKeysFromArray(raw) : raw && typeof raw === "object" ? Object.keys(raw) : []);
            const count = testResult?.count ?? (Array.isArray(raw) ? raw.length : (raw == null ? 0 : 1));
            return (
              <div style={{ background: T.bg, border: `1px solid ${(testResult && testResult.ok === false) ? T.red + "44" : T.green + "44"}`, borderRadius: 4, padding: 6, marginTop: 6 }}>
                {testResult && testResult.ok === false
                  ? <div style={{ fontSize: 8, color: T.red }}>Error: {testResult.error}</div>
                  : <>
                    <div style={{ fontSize: 8, color: T.green, fontWeight: 700, marginBottom: 4 }}>✓ Test Result: {count} row/item{count === 1 ? "" : "s"}</div>
                    <div style={{ fontSize: 8, color: T.blue, fontWeight: 700, marginBottom: 3 }}>Columns / Keys (use as dynamic row variables)</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 3, marginBottom: 5 }}>
                      {keys.length
                        ? keys.map((k) => <span key={k} style={{ fontSize: 8, color: T.blueLt, background: T.bgInput, border: `1px solid ${T.border}`, borderRadius: 3, padding: "1px 5px", fontFamily: "monospace" }}>{k}</span>)
                        : <span style={{ fontSize: 8, color: T.textMute }}>No object keys detected.</span>}
                    </div>
                    <div style={{ fontSize: 7, color: T.textMute, marginBottom: 2, textTransform: "uppercase", letterSpacing: "0.07em" }}>Sample</div>
                    <div style={{ fontSize: 8, color: T.textSub, maxHeight: 84, overflow: "auto", fontFamily: "monospace", wordBreak: "break-all" }}>
                      {JSON.stringify(testResult?.sample ?? raw).slice(0, 600)}
                    </div>
                  </>}
              </div>
            );
          })()}
        </div>
      ))}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, marginTop: 4 }}>
        <button onClick={() => add("api")} style={{ width: "100%", padding: 6, background: "transparent", border: `1px dashed ${T.green}55`, borderRadius: 4, color: T.green, cursor: "pointer", fontSize: 9 }}>+ API</button>
        <button onClick={() => add("rss")} style={{ width: "100%", padding: 6, background: "transparent", border: `1px dashed ${T.blue}55`, borderRadius: 4, color: T.blue, cursor: "pointer", fontSize: 9 }}>+ RSS</button>
        <button onClick={() => add("googleSheets")} style={{ width: "100%", padding: 6, background: "transparent", border: `1px dashed ${T.yellow}55`, borderRadius: 4, color: T.yellow, cursor: "pointer", fontSize: 9 }}>+ Google Sheets</button>
        <button onClick={() => add("custom")} style={{ width: "100%", padding: 6, background: "transparent", border: `1px dashed ${T.accentLt}55`, borderRadius: 4, color: T.accentLt, cursor: "pointer", fontSize: 9 }}>+ Custom</button>
      </div>
    </div>
  );
}